import { useState } from 'react';
import { alertStyle, buttonStyle } from './changeOrderStyles';
import ChangeOrderRecordWorkflows from './ChangeOrderRecordWorkflows';

const statusLabels = {
  draft: 'Draft',
  sent: 'Sent to customer',
  customer_signed: 'Customer signed',
  completed: 'Completed',
  void: 'Voided',
};
const statusStyle = (status) => ({
  fontSize: 11,
  fontWeight: 700,
  borderRadius: 20,
  padding: '5px 9px',
  color: status === 'completed' ? '#236347' : status === 'void' ? '#8a4545' : '#315474',
  background: status === 'completed' ? '#e5f3eb' : status === 'void' ? '#f8eaea' : '#e9f0f7',
  whiteSpace: 'nowrap',
});
function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export default function ChangeOrderRecords({
  items,
  jobId,
  customer,
  address,
  customerEmail,
  busyId,
  setBusyId,
  request,
  loadItems,
  openPreview,
  previews,
  downloadSource,
  setActionError,
  onEditRevision,
  importedContracts = [],
}) {
  const [sendId, setSendId] = useState(null);
  const [sendConfirm, setSendConfirm] = useState({ job: false, document: false, recipient: false });
  const [selectedContracts, setSelectedContracts] = useState({});
  const [linkingId, setLinkingId] = useState(null);
  const [voidId, setVoidId] = useState(null);
  const [voidReason, setVoidReason] = useState('');
  const [voidConfirmed, setVoidConfirmed] = useState(false);
  const [signatureId, setSignatureId] = useState(null);
  const [signName, setSignName] = useState('');
  const [signInitials, setSignInitials] = useState('');
  const [signConsent, setSignConsent] = useState(false);

  const canRevise = (item) =>
    item.source_kind === 'native' &&
    ['draft', 'void'].includes(item.status) &&
    !!item.document_hash &&
    !items.some(
      (later) => String(later.revision_of ?? later.editor_data?.revision_of) === String(item.id),
    );

  const linkContract = async (item) => {
    const selectedId = selectedContracts[item.id];
    const selectedContract = importedContracts.find(
      (contract) => String(contract.id) === String(selectedId),
    );
    if (!selectedContract || item.status !== 'draft' || item.imported_contract) return;
    setActionError('');
    setLinkingId(item.id);
    try {
      await request(
        `/api/jobs/${encodeURIComponent(jobId)}/change-orders/${encodeURIComponent(item.id)}/contract`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contract_id: selectedContract.id,
            document_hash: item.document_hash,
            confirm_job_id: jobId,
          }),
        },
      );
      setSelectedContracts((current) => ({ ...current, [item.id]: '' }));
      await loadItems();
    } catch (error) {
      setActionError(error.message || 'Unable to link the imported contract. You can retry.');
    } finally {
      setLinkingId(null);
    }
  };

  const sendChangeOrder = async (item) => {
    if (!Object.values(sendConfirm).every(Boolean)) return;
    setActionError('');
    setBusyId(item.id);
    try {
      await request(
        `/api/jobs/${encodeURIComponent(jobId)}/change-orders/${encodeURIComponent(item.id)}/send`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: customerEmail,
            reviewed: true,
            confirm_job_id: jobId,
            document_hash: item.document_hash,
          }),
        },
      );
      setSendId(null);
      await loadItems();
    } catch (error) {
      setActionError(error.message || 'Unable to send the change order. You can retry this draft.');
      await loadItems();
    } finally {
      setBusyId(null);
    }
  };

  const voidChangeOrder = async (item) => {
    if (!voidReason.trim()) {
      setActionError('Enter a reason before voiding this record.');
      return;
    }
    setActionError('');
    setBusyId(item.id);
    try {
      await request(
        `/api/jobs/${encodeURIComponent(jobId)}/change-orders/${encodeURIComponent(item.id)}/void`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reason: voidReason.trim() }),
        },
      );
      setVoidId(null);
      setVoidReason('');
      await loadItems();
    } catch (error) {
      setActionError(error.message || 'Unable to void this change order.');
    } finally {
      setBusyId(null);
    }
  };

  const countersign = async (item) => {
    if (!signName.trim() || !signConsent || (item.initial_pages?.length && !signInitials.trim()))
      return;
    setActionError('');
    setBusyId(item.id);
    try {
      await request(
        `/api/jobs/${encodeURIComponent(jobId)}/change-orders/${encodeURIComponent(item.id)}/countersign`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: signName.trim(),
            initials: signInitials.trim(),
            consent: true,
            document_hash: item.document_hash,
          }),
        },
      );
      setSignatureId(null);
      setSignName('');
      setSignInitials('');
      setSignConsent(false);
      await loadItems();
    } catch (error) {
      setActionError(error.message || 'Unable to countersign this change order.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div style={{ marginTop: 20 }}>
      <h4 style={{ margin: '0 0 10px', color: '#26364d', fontSize: 14 }}>Records</h4>
      {!items.length ? (
        <div
          style={{
            padding: 18,
            border: '1px dashed #c5d1e0',
            borderRadius: 8,
            color: '#6b778a',
            fontSize: 13,
          }}
        >
          No uploaded change orders for this job yet. A new upload will be saved as a draft.
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 10 }}>
          {items.map((item) => (
            <article
              key={item.id}
              style={{
                border: '1px solid #dbe3ef',
                background: '#fff',
                borderRadius: 9,
                padding: 14,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'wrap',
                }}
              >
                <div>
                  <strong style={{ color: '#253650' }}>
                    {item.title || 'Untitled change order'}
                  </strong>
                  <div style={{ color: '#6b778a', fontSize: 12, marginTop: 4 }}>
                    {item.change_number ? `${item.change_number} · ` : ''}
                    {item.page_count || '—'} source pages
                    {['customer_signed', 'completed'].includes(item.status)
                      ? ' + signature record'
                      : ''}{' '}
                    · {item.recipient_email || 'No recipient recorded'}
                    {item.source_kind === 'native' ? ' · Native' : ''}
                  </div>
                  <div style={{ color: '#6b778a', fontSize: 12, marginTop: 4 }}>
                    Customer signed: {formatDate(item.customer_signed_at)} · Contractor signed:{' '}
                    {formatDate(item.contractor_signed_at)}
                  </div>
                  {item.imported_contract && (
                    <div
                      style={{
                        marginTop: 9,
                        padding: '8px 10px',
                        borderLeft: '3px solid #1b6b56',
                        borderRadius: 5,
                        background: '#edf7f2',
                        color: '#235b49',
                        fontSize: 12,
                      }}
                    >
                      <strong>Linked signed contract:</strong> {item.imported_contract.contract_number} ·{' '}
                      {item.imported_contract.title}
                      {item.imported_contract.signed_on
                        ? ` · Signed ${formatDate(item.imported_contract.signed_on)}`
                        : ''}
                    </div>
                  )}
                </div>
                <span style={statusStyle(item.status)}>
                  {statusLabels[item.status] || item.status || 'Unknown'}
                </span>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => openPreview(item)}
                  disabled={busyId === item.id}
                  style={buttonStyle('quiet')}
                >
                  {busyId === item.id
                    ? 'Loading PDF…'
                    : ['customer_signed', 'completed'].includes(item.status)
                      ? 'Preview signed PDF'
                      : 'Preview source PDF'}
                </button>
                {previews[item.id] && (
                  <button
                    type="button"
                    onClick={() => downloadSource(item)}
                    style={buttonStyle('quiet')}
                  >
                    Download PDF
                  </button>
                )}
                {canRevise(item) && (
                  <button
                    type="button"
                    onClick={() => onEditRevision(item)}
                    style={buttonStyle('quiet')}
                  >
                    Edit revision
                  </button>
                )}
                {item.status === 'draft' && (
                  <button
                    type="button"
                    onClick={() => {
                      setSendId(sendId === item.id ? null : item.id);
                      setSendConfirm({ job: false, document: false, recipient: false });
                      setActionError('');
                    }}
                    style={buttonStyle('primary')}
                  >
                    Review &amp; send
                  </button>
                )}
                {item.status === 'customer_signed' && (
                  <button
                    type="button"
                    onClick={() => setSignatureId(signatureId === item.id ? null : item.id)}
                    style={buttonStyle('primary')}
                  >
                    Countersign
                  </button>
                )}
                {['draft', 'sent', 'customer_signed'].includes(item.status) && (
                  <button
                    type="button"
                    onClick={() => {
                      setVoidId(voidId === item.id ? null : item.id);
                      setVoidReason('');
                      setVoidConfirmed(false);
                      setActionError('');
                    }}
                    style={buttonStyle('danger')}
                  >
                    Void record
                  </button>
                )}
              </div>
              {item.status === 'draft' && !item.imported_contract && importedContracts.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'end',
                    gap: 8,
                    flexWrap: 'wrap',
                    marginTop: 11,
                    padding: 10,
                    border: '1px solid #dbe3ef',
                    borderRadius: 7,
                    background: '#f8fafc',
                  }}
                >
                  <label style={{ display: 'grid', gap: 5, flex: '1 1 240px', color: '#43546c', fontSize: 12, fontWeight: 700 }}>
                    Link an existing signed contract
                    <select
                      value={selectedContracts[item.id] || ''}
                      onChange={(event) =>
                        setSelectedContracts((current) => ({ ...current, [item.id]: event.target.value }))
                      }
                      style={{
                        minHeight: 38,
                        border: '1px solid #c8d4e4',
                        borderRadius: 7,
                        padding: '7px 9px',
                        background: '#fff',
                        color: '#23344d',
                        font: 'inherit',
                      }}
                    >
                      <option value="">Choose imported contract…</option>
                      {importedContracts.map((contract) => (
                        <option key={contract.id} value={contract.id}>
                          {contract.contract_number} — {contract.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button
                    type="button"
                    onClick={() => linkContract(item)}
                    disabled={!selectedContracts[item.id] || linkingId === item.id}
                    style={buttonStyle('quiet')}
                  >
                    {linkingId === item.id ? 'Linking…' : 'Link contract'}
                  </button>
                </div>
              )}
              {previews[item.id] && (
                <iframe
                  title={`${item.title || 'Change order'} source PDF`}
                  src={previews[item.id].url}
                  style={{
                    width: '100%',
                    height: 420,
                    border: '1px solid #dbe3ef',
                    borderRadius: 7,
                    marginTop: 10,
                  }}
                />
              )}
              {item.last_send_error && item.status === 'draft' && (
                <div role="alert" style={{ ...alertStyle, marginTop: 10 }}>
                  Previous send failed: {item.last_send_error}. This draft can be reviewed and
                  retried.
                </div>
              )}
              <ChangeOrderRecordWorkflows
                item={item}
                jobId={jobId}
                customer={customer}
                address={address}
                customerEmail={customerEmail}
                busyId={busyId}
                sendOpen={sendId === item.id}
                sendConfirm={sendConfirm}
                setSendConfirm={setSendConfirm}
                onSend={sendChangeOrder}
                onCloseSend={() => setSendId(null)}
                openPreview={openPreview}
                voidOpen={voidId === item.id}
                voidReason={voidReason}
                setVoidReason={setVoidReason}
                voidConfirmed={voidConfirmed}
                setVoidConfirmed={setVoidConfirmed}
                onVoid={voidChangeOrder}
                onCloseVoid={() => setVoidId(null)}
                signatureOpen={signatureId === item.id}
                signName={signName}
                setSignName={setSignName}
                signInitials={signInitials}
                setSignInitials={setSignInitials}
                signConsent={signConsent}
                setSignConsent={setSignConsent}
                onCountersign={countersign}
                onCloseSignature={() => setSignatureId(null)}
              />
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
